/* 
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

import DisclaimerFooter from "components/DisclaimerFooter"
import { useEffect } from "react"
import { Link } from "react-router"
import { pageMetaData, updateMetaTags } from "utils/metaTags"

const Contact = () => {
  // Update meta tags for contact page
  useEffect(() => {
    const { title, description, keywords } = pageMetaData.contact
    updateMetaTags(title, description, keywords)
  }, [])
  return (
    <div className="pt-1 pb-3">
      <h1 className="fs-4 pb-3">Contact Us</h1>

      <div className="card shadow-sm p-4 mb-4">
        <h2 className="fs-5 mb-3">Email</h2>
        <p className="mb-2">For questions, suggestions, or support:</p>
        <p className="d-flex align-items-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            fill="currentColor"
            className="bi bi-envelope me-2"
            viewBox="0 0 16 16"
          >
            <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4Zm2-1a1 1 0 0 0-1 1v.217l7 4.2 7-4.2V4a1 1 0 0 0-1-1H2Zm13 2.383-4.708 2.825L15 11.105V5.383Zm-.034 6.876-5.64-3.471L8 9.583l-1.326-.795-5.64 3.47A1 1 0 0 0 2 13h12a1 1 0 0 0 .966-.741ZM1 11.105l4.708-2.897L1 5.383v5.722Z" />
          </svg>
          <a href="mailto:support&#64;saferetirementspending&#46;com">
            support&#64;saferetirementspending&#46;com
          </a>
        </p>
        <p className="mb-0 text-muted">
          <small>
            Response times may vary depending on message volume, but we aim to reply as promptly as
            possible.
          </small>
        </p>
      </div>

      <div className="card mb-4">
        <div className="card-header">
          <h2 className="fs-5 mb-0">Frequently Asked Questions</h2>
        </div>
        <div className="card-body">
          <h3 className="fs-6 fw-bold mb-2">Do you record my personal data?</h3>
          {/* AIDEV-NOTE: #1027 - this answer used to be a flat "No.", which contradicted the
              page it links to: Privacy.tsx's "Information We Collect" lists Device Data (IP
              address, browser, OS), Usage Data and Error and Performance Data as collected
              automatically, and an IP address is personal data under GDPR and the CCPA. Two
              questions were being conflated - tracking/advertising, where the answer really is
              no, and operational/diagnostic data, where it is not. Keep both halves; a denial
              of either one alone is false.
              The FAQ must not claim more than Privacy.tsx does, in either direction, so the
              calculator-input clause reuses that page's own words verbatim ("not stored in a
              database", Privacy.tsx Data Storage and Security; "after your session", the
              Calculator Inputs bullet under Information We Collect). An unqualified "nothing
              you enter is stored" was the reason this note exists a second time: it reads as
              a promise that no request body ever reaches disk anywhere, which is stronger
              than the policy and stronger than the code can support. Even the narrower claim
              is only safe because #1022 landed (backend/swr_toolbox/main.py,
              max_request_body_size="never" and include_local_variables=False); if either is
              ever relaxed, this sentence becomes false. Guarded by the personal-data denial
              pattern in utils/legalCopy.test.ts. */}
          <p className="mb-4 ps-3">
            No advertising, analytics or tracking cookies of any kind, and your calculator inputs
            are not stored in a database or retained after your session. We do collect basic
            technical data needed to run and debug the site, including IP addresses in server logs —
            see the <Link to="/privacy/">privacy page</Link> for what we collect and why.
          </p>

          <h3 className="fs-6 fw-bold mb-2">What sources does your data come from?</h3>
          <p className="mb-4 ps-3">
            See our <Link to="/methodology/">methodology page</Link> for current data ranges.
          </p>

          <h3 className="fs-6 fw-bold mb-2">
            How is the site different from other retirement calculators?
          </h3>
          <p className="mb-0 ps-3">
            Learn more about what makes our approach different&nbsp;
            <Link to="/about/">here</Link>.
          </p>
        </div>
      </div>
      <DisclaimerFooter />
    </div>
  )
}

export default Contact
